import { useEffect, useState, useCallback } from "react";
import { Navbar } from "@/components/Navbar";
import { api, naira, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, FileText, Plus, RefreshCw, Copy, Building2, Mail, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

function StatusBadge({ status }) {
  const s = (status || "").toUpperCase();
  const cls = s === "PAID" ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800";
  return <Badge className={cls} data-testid={`invoice-status-${s}`}>{s.replace(/_/g, " ") || "—"}</Badge>;
}

function ReservedAccounts({ reserved, error, onRetry, retrying }) {
  const accounts = (reserved && reserved.accounts) || [];
  const copy = (v) => { navigator.clipboard.writeText(v); toast.success("Copied"); };
  if (!accounts.length) {
    return (
      <div className="space-y-1.5">
        <span className="text-xs text-gray-400 block">No reserved account yet</span>
        {error && <span className="text-xs text-red-500 block">{error}</span>}
        <Button size="sm" variant="outline" data-testid="retry-reserved-btn" disabled={retrying} onClick={onRetry} className="border-emerald-200 text-emerald-800 h-7 text-xs">
          {retrying ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <RefreshCw className="w-3 h-3 mr-1" />} Generate account
        </Button>
      </div>
    );
  }
  return (
    <div className="space-y-2">
      {accounts.map((a, i) => (
        <div key={a.accountNumber || `${a.bankName}-${i}`} className="rounded-lg border border-emerald-100 bg-emerald-50/50 px-3 py-2 text-xs" data-testid={`reserved-account-${i}`}>
          <div className="flex items-center gap-1.5 font-semibold text-emerald-900"><Building2 className="w-3.5 h-3.5" />{a.bankName || "—"}</div>
          <div className="mt-1 flex items-center gap-2">
            <span className="font-mono tabular-nums text-sm text-emerald-950">{a.accountNumber || "—"}</span>
            {a.accountNumber && (
              <button onClick={() => copy(a.accountNumber)} className="text-emerald-600 hover:text-emerald-800" data-testid={`copy-acct-${i}`}><Copy className="w-3.5 h-3.5" /></button>
            )}
          </div>
          <div className="text-gray-500">{a.accountName || "—"}</div>
        </div>
      ))}
    </div>
  );
}

export default function AdminInvoices() {
  const [invoices, setInvoices] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [busy, setBusy] = useState(false);
  const [retryId, setRetryId] = useState(null);
  const [emailId, setEmailId] = useState(null);
  const [confirmId, setConfirmId] = useState(null);
  const [programmes, setProgrammes] = useState([]);
  const [form, setForm] = useState({ sponsor_name: "", sponsor_email: "", amount: "", description: "", programme_id: "" });

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/invoices");
      setInvoices(data.invoices || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoaded(true); }
  }, []);
  useEffect(() => { load(); api.get("/admin/programmes").then((r) => setProgrammes(r.data.programmes || [])).catch(() => {}); }, [load]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const retryReserved = async (invoiceId) => {
    setRetryId(invoiceId);
    try {
      await api.post(`/admin/invoices/${invoiceId}/reserved-account`);
      toast.success("Reserved account generated");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setRetryId(null); }
  };

  const emailSponsor = async (invoiceId) => {
    setEmailId(invoiceId);
    try {
      const { data } = await api.post(`/admin/invoices/${invoiceId}/email`);
      toast.success(data.sent ? `Email sent to ${data.to}` : `Email queued for ${data.to} (delivery pending)`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setEmailId(null); }
  };

  const confirmPayment = async (invoiceId) => {
    if (!window.confirm("Confirm payment received for this invoice? This will activate the linked programme's beneficiaries and enable their vouchers for redemption.")) return;
    setConfirmId(invoiceId);
    try {
      const { data } = await api.post(`/admin/invoices/${invoiceId}/confirm-payment`);
      toast.success(`Payment confirmed — ${data.beneficiaries_activated} beneficiaries activated`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setConfirmId(null); }
  };

  const createInvoice = async (e) => {
    e.preventDefault();
    const amount = parseInt(form.amount, 10);
    if (!form.sponsor_name.trim() || !form.sponsor_email.trim() || !amount || amount <= 0) {
      toast.error("Enter sponsor name, email and a valid amount");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/admin/invoices", {
        sponsor_name: form.sponsor_name.trim(),
        sponsor_email: form.sponsor_email.trim(),
        amount,
        description: form.description.trim(),
        programme_id: form.programme_id || null,
      });
      toast.success(`Invoice ${data.invoice.invoice_id} created`);
      setForm({ sponsor_name: "", sponsor_email: "", amount: "", description: "", programme_id: "" });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  if (!loaded) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-emerald-600" /></div>;

  const totalOutstanding = invoices.filter((i) => i.status !== "PAID").reduce((a, i) => a + (i.amount || 0), 0);
  const totalPaid = invoices.filter((i) => i.status === "PAID").reduce((a, i) => a + (i.paid_amount || i.amount || 0), 0);

  return (
    <div>
      <Navbar />
      <div className="max-w-6xl mx-auto px-4 py-10">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <span className="text-xs font-mono uppercase tracking-widest text-emerald-700">Sponsor Funding · Invoices</span>
            <h1 className="mt-2 font-display font-bold text-3xl text-emerald-950">Sponsor Invoices</h1>
            <p className="mt-1 text-sm text-gray-500">Issue an invoice with a dedicated Monnify bank account for the sponsor to pay into.</p>
          </div>
          <Button data-testid="refresh-invoices-btn" variant="outline" onClick={load} className="border-emerald-200 text-emerald-800">
            <RefreshCw className="w-4 h-4 mr-2" /> Refresh
          </Button>
        </div>

        <div className="mt-6 grid gap-4 sm:grid-cols-3">
          <div className="rounded-2xl border border-emerald-100 bg-white p-5" data-testid="invoices-total-card">
            <span className="text-xs text-gray-500 font-medium">Total Invoices</span>
            <div className="mt-2 font-bold text-2xl text-emerald-950 tabular-nums">{invoices.length}</div>
          </div>
          <div className="rounded-2xl border border-emerald-100 bg-white p-5" data-testid="invoices-outstanding-card">
            <span className="text-xs text-gray-500 font-medium">Awaiting Payment</span>
            <div className="mt-2 font-bold text-2xl text-amber-700 tabular-nums">{naira(totalOutstanding)}</div>
          </div>
          <div className="rounded-2xl border border-emerald-100 bg-white p-5" data-testid="invoices-paid-card">
            <span className="text-xs text-gray-500 font-medium">Total Received</span>
            <div className="mt-2 font-bold text-2xl text-emerald-700 tabular-nums">{naira(totalPaid)}</div>
          </div>
        </div>

        <form onSubmit={createInvoice} className="mt-8 rounded-2xl border border-emerald-100 bg-white p-6" data-testid="create-invoice-form">
          <div className="flex items-center gap-2 text-emerald-900 font-semibold"><Plus className="w-4 h-4" /> New Invoice</div>
          <div className="mt-4 grid gap-4 sm:grid-cols-2">
            <div>
              <Label htmlFor="sponsor_name">Sponsor Name</Label>
              <Input id="sponsor_name" data-testid="invoice-sponsor-name" value={form.sponsor_name} onChange={set("sponsor_name")} placeholder="e.g. Dangote Foundation" className="mt-1" />
            </div>
            <div>
              <Label htmlFor="sponsor_email">Sponsor Email</Label>
              <Input id="sponsor_email" type="email" data-testid="invoice-sponsor-email" value={form.sponsor_email} onChange={set("sponsor_email")} placeholder="finance@sponsor.org" className="mt-1" />
            </div>
            <div>
              <Label htmlFor="amount">Amount (NGN)</Label>
              <Input id="amount" type="number" min="1" data-testid="invoice-amount" value={form.amount} onChange={set("amount")} placeholder="5000000" className="mt-1" />
            </div>
            <div>
              <Label htmlFor="description">Description (optional)</Label>
              <Input id="description" data-testid="invoice-description" value={form.description} onChange={set("description")} placeholder="Q2 2026 food programme funding" className="mt-1" />
            </div>
            <div>
              <Label>Programme to activate on payment</Label>
              <Select value={form.programme_id || "__none__"} onValueChange={(v) => setForm((p) => ({ ...p, programme_id: v === "__none__" ? "" : v }))}>
                <SelectTrigger data-testid="invoice-programme" className="mt-1"><SelectValue placeholder="None (no auto-activation)" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">None (no auto-activation)</SelectItem>
                  {programmes.map((p) => <SelectItem key={p.id} value={p.id}>{p.name} · {p.code}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <Button type="submit" data-testid="submit-invoice-btn" disabled={busy} className="mt-5 bg-emerald-600 hover:bg-emerald-700">
            {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileText className="w-4 h-4 mr-2" />} Create Invoice
          </Button>
        </form>

        <div className="mt-8 rounded-2xl border border-emerald-100 bg-white overflow-hidden">
          <div className="px-6 py-4 border-b border-emerald-50 font-semibold text-emerald-900">Invoices</div>
          {invoices.length === 0 ? (
            <div className="px-6 py-12 text-center text-gray-400 text-sm" data-testid="invoices-empty">No invoices yet. Create one above.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="invoices-table">
                <thead>
                  <tr className="text-left text-xs uppercase text-gray-400 border-b border-emerald-50">
                    <th className="px-6 py-3 font-medium">Invoice</th>
                    <th className="px-6 py-3 font-medium">Sponsor</th>
                    <th className="px-6 py-3 font-medium">Amount</th>
                    <th className="px-6 py-3 font-medium">Status</th>
                    <th className="px-6 py-3 font-medium">Reserved Bank Account</th>
                    <th className="px-6 py-3 font-medium">Payment</th>
                    <th className="px-6 py-3 font-medium text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {invoices.map((inv) => {
                    const paid = inv.status === "PAID";
                    return (
                    <tr key={inv.invoice_id} className="border-b border-emerald-50 last:border-0 align-top" data-testid={`invoice-row-${inv.invoice_id}`}>
                      <td className="px-6 py-4">
                        <div className="font-mono text-emerald-900">{inv.invoice_id}</div>
                        {inv.programme_name && <div className="text-xs text-emerald-600 mt-0.5">{inv.programme_name}</div>}
                        {inv.description && <div className="text-xs text-gray-400 mt-0.5">{inv.description}</div>}
                      </td>
                      <td className="px-6 py-4">
                        <div className="font-medium text-emerald-950">{inv.sponsor_name}</div>
                        <div className="text-xs text-gray-400">{inv.sponsor_email}</div>
                      </td>
                      <td className="px-6 py-4 font-semibold tabular-nums text-emerald-950">{naira(inv.amount)}</td>
                      <td className="px-6 py-4"><StatusBadge status={inv.status} /></td>
                      <td className="px-6 py-4 min-w-[240px]"><ReservedAccounts reserved={inv.reserved_account} error={inv.reserved_error} retrying={retryId === inv.invoice_id} onRetry={() => retryReserved(inv.invoice_id)} /></td>
                      <td className="px-6 py-4 min-w-[180px]" data-testid={`invoice-payment-${inv.invoice_id}`}>
                        {paid ? (
                          <div className="text-xs space-y-0.5">
                            <div className="flex items-center gap-1 text-emerald-700 font-semibold"><CheckCircle2 className="w-3.5 h-3.5" />{naira(inv.paid_amount || inv.amount)} received</div>
                            {inv.paid_reference && <div className="text-gray-500">Ref: <span className="font-mono">{inv.paid_reference}</span></div>}
                            {inv.paid_at && <div className="text-gray-400">{new Date(inv.paid_at).toLocaleString()}</div>}
                          </div>
                        ) : (
                          <Button size="sm" data-testid={`confirm-payment-btn-${inv.invoice_id}`} disabled={confirmId === inv.invoice_id} onClick={() => confirmPayment(inv.invoice_id)} className="h-8 text-xs bg-emerald-600 hover:bg-emerald-700">
                            {confirmId === inv.invoice_id ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5 mr-1" />} Confirm Payment
                          </Button>
                        )}
                      </td>
                      <td className="px-6 py-4 text-right">
                        <Button size="sm" variant="outline" data-testid={`email-invoice-btn-${inv.invoice_id}`} disabled={emailId === inv.invoice_id} onClick={() => emailSponsor(inv.invoice_id)} className="border-emerald-200 text-emerald-800 h-8 text-xs">
                          {emailId === inv.invoice_id ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Mail className="w-3.5 h-3.5 mr-1" />} Email sponsor
                        </Button>
                        {inv.emailed_at && <div className="text-[11px] text-gray-400 mt-1">Sent {new Date(inv.emailed_at).toLocaleDateString()}</div>}
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
